import { useEffect, useState } from "react";
import { Settings, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, toastErr } from "@/lib/api";
import { Card, Label, inputCls, PrimaryButton } from "./ui";
import { LogoUpload } from "./LogoUpload";

export const PersonaTab = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)).catch(toastErr); }, []);
  if (!cfg) return <Loader2 className="animate-spin text-slate-400" />;

  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.type === "range" || e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { bot_name, system_prompt, tone, guardrails, temperature, max_tokens, kb_char_limit, retrieval_k } = cfg;
      await api.put("/admin/config", { bot_name, system_prompt, tone, guardrails, temperature, max_tokens, kb_char_limit, retrieval_k });
      toast.success("Configuration saved");
    } catch (e) { toastErr(e); } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-6">
        <Card title="Core Persona">
          <Label>Bot name</Label>
          <input data-testid="bot-name-input" className={inputCls} value={cfg.bot_name} onChange={set("bot_name")} />
          <LogoUpload />
          <div className="mt-5"><Label>System prompt</Label>
            <textarea data-testid="system-prompt-textarea" rows={7} className={inputCls} value={cfg.system_prompt} onChange={set("system_prompt")} /></div>
          <div className="mt-5"><Label>Tone & style</Label>
            <input data-testid="tone-input" className={inputCls} value={cfg.tone} onChange={set("tone")} /></div>
        </Card>
        <Card title="Safety & Constraints">
          <Label>Guardrails</Label>
          <textarea data-testid="guardrails-textarea" rows={4} className={inputCls} value={cfg.guardrails} onChange={set("guardrails")} />
        </Card>
      </div>
      <div className="space-y-6">
        <Card title="Model Parameters">
          <div className="flex justify-between items-center mb-2"><Label>Temperature</Label><span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{cfg.temperature}</span></div>
          <input data-testid="temperature-slider" type="range" min="0" max="2" step="0.1" value={cfg.temperature} onChange={set("temperature")} className="w-full" />
          <div className="flex justify-between items-center mt-5 mb-2"><Label>Max tokens</Label><span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{cfg.max_tokens}</span></div>
          <input data-testid="max-tokens-slider" type="range" min="128" max="8192" step="128" value={cfg.max_tokens} onChange={set("max_tokens")} className="w-full" />
          <div className="flex justify-between items-center mt-5 mb-2"><Label>Always-included chars</Label><span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{cfg.kb_char_limit}</span></div>
          <input data-testid="kb-limit-slider" type="range" min="2000" max="100000" step="1000" value={cfg.kb_char_limit} onChange={set("kb_char_limit")} className="w-full" />
          <div className="flex justify-between items-center mt-5 mb-2"><Label>Passages per answer</Label><span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{cfg.retrieval_k ?? 8}</span></div>
          <input data-testid="retrieval-k-slider" type="range" min="2" max="20" step="1" value={cfg.retrieval_k ?? 8} onChange={set("retrieval_k")} className="w-full" />
        </Card>
        <PrimaryButton data-testid="save-configuration-button" onClick={save} disabled={saving} className="w-full py-3">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Settings size={16} />} Save Configuration
        </PrimaryButton>
      </div>
    </div>
  );
};
